<script lang="ts">
    export interface FileFilter {
        type: 'include' | 'exclude';
        extensions: string[];
    }
</script>

<script setup lang="ts">
    interface Props {
        files: FileFilter;
        title?: string;
        disabled?: boolean;
        acceptLabel?: string;
        // Optional constraint line (e.g. "JPG, PNG ou WebP · 3 Mo max · au moins 300 × 300 px"),
        // shown in place of `acceptLabel`. Used by the image upload flows; other callers keep `acceptLabel`.
        hint?: string;
        multiple?: boolean;
    }

    const props = withDefaults(defineProps<Props>(), {
        title: 'invoicing.expense.add_file_modal.dropzone_title',
        disabled: false,
        // No default label — callers pass their own format hint (or the constraint `hint`); avoids a
        // hardcoded user-facing string and a misleading "(pdf)" on non-PDF drop zones.
        acceptLabel: undefined,
        hint: undefined,
        multiple: false,
    });

    const emit = defineEmits<{
        (e: 'files-selected', files: File[]): void;
    }>();

    const isDragging = ref(false);
    const fileInput = ref<HTMLInputElement | null>(null);

    const inputAccept = computed(() => {
        if (props.files.type === 'include') {
            return props.files.extensions.map((ext) => `.${ext}`).join(',');
        }
        return undefined;
    });

    const handleDragEnter = (e: DragEvent) => {
        if (props.disabled) return;
        e.preventDefault();
        isDragging.value = true;
    };

    const handleDragLeave = (e: DragEvent) => {
        if (props.disabled) return;
        e.preventDefault();
        if (e.currentTarget === e.target) {
            isDragging.value = false;
        }
    };

    const handleDragOver = (e: DragEvent) => {
        if (props.disabled) return;
        e.preventDefault();
    };

    const handleDrop = (e: DragEvent) => {
        if (props.disabled) return;
        e.preventDefault();
        isDragging.value = false;

        const files = e.dataTransfer?.files;
        if (!files || files.length === 0) return;

        processFiles(files);
    };

    const handleFileInput = (e: Event) => {
        const input = e.target as HTMLInputElement;
        const files = input.files;
        if (!files || files.length === 0) return;

        processFiles(files);

        // Reset input to allow selecting the same file again
        if (input) {
            input.value = '';
        }
    };

    const processFiles = (fileList: FileList) => {
        const allFiles = Array.from(fileList);
        const validFiles = allFiles.filter((file) => isValidFileType(file));

        if (validFiles.length !== allFiles.length) {
            useToasteoError('form.file_upload.invalid_file_type');
        }

        if (validFiles.length === 0) return;

        emit('files-selected', props.multiple ? validFiles : [validFiles[0]]);
    };

    const isValidFileType = (file: File): boolean => {
        const extension = file.name.split('.').pop()?.toLowerCase() || '';

        if (props.files.type === 'exclude') {
            return !props.files.extensions.map((ext) => ext.toLowerCase()).includes(extension);
        }

        return props.files.extensions.map((ext) => ext.toLowerCase()).includes(extension);
    };

    const openFileDialog = () => {
        if (!props.disabled) {
            fileInput.value?.click();
        }
    };
</script>

<template>
    <div
        class="relative"
        @dragenter="handleDragEnter"
        @dragleave="handleDragLeave"
        @dragover="handleDragOver"
        @drop="handleDrop"
    >
        <div
            class="border-2 border-dashed rounded-lg p-8 text-center transition-all cursor-pointer border-neutral-300 hover:border-neutral-400"
            :class="[isDragging ? 'border-branding-green bg-branding-green/5' : '', disabled ? 'opacity-50 cursor-not-allowed' : '']"
            @click="openFileDialog"
        >
            <input
                ref="fileInput"
                type="file"
                :accept="inputAccept"
                :multiple="multiple"
                class="hidden"
                :disabled="disabled"
                @change="handleFileInput"
            />
            <div class="flex flex-col items-center gap-4">
                <i class="fa-regular fa-cloud-upload text-4xl text-neutral-400"></i>
                <div>
                    <p class="text-sm font-medium text-neutral-700">
                        {{ title }}
                    </p>
                    <p
                        v-if="hint || acceptLabel"
                        class="text-xs text-neutral-500 mt-1"
                    >
                        {{ hint || acceptLabel }}
                    </p>
                </div>
            </div>
        </div>
    </div>
</template>
